import { request, type IRequestConfig } from '@/api';
import type {
  SaveMaterialDto,
  UpdateMaterialDto,
  BatchUpdateMaterialDto,
  BatchMoveMaterialDto,
  BatchDeleteMaterialDto,
  MaterialPageDto,
  MaterialPageRes,
  FindReplaceMaterialPageDto,
  FindReplaceMaterialPageRecord,
  FindReplaceMaterialDto,
  FindReplaceMaterialResult,
  MaterialExportDto,
  SaveMaterialResp,
} from './types';
import type { PaginationData } from '@/types/api';

export const materialApi = {
  /** 保存已上传素材，可选配置用于抽屉确认关闭后取消当前入库请求 */
  save: (data: SaveMaterialDto, config?: IRequestConfig) => {
    return request.post<SaveMaterialResp>('/creative/material/save', data, config);
  },

  /** 更新素材，可选配置用于编辑弹窗取消当前保存请求 */
  update: (data: UpdateMaterialDto, config?: IRequestConfig) => {
    return request.post<string>('/creative/material/update', data, config);
  },

  /** 批量更新素材，与单条编辑共用请求取消配置 */
  batchUpdate: (data: BatchUpdateMaterialDto, config?: IRequestConfig) => {
    return request.post<string>('/creative/material/batch/update', data, config);
  },

  /** 单条和批量移动共用接口，可选配置用于取消当前弹窗请求 */
  batchMove: (data: BatchMoveMaterialDto, config?: IRequestConfig) => {
    return request.post<string>('/creative/material/batch/move', data, config);
  },

  // 批量删除素材
  batchDelete: (data: BatchDeleteMaterialDto) => {
    return request.post<string>('/creative/material/batch/delete', data);
  },

  /**
   * 素材名称查找替换-查找匹配素材
   * /creative/material/findReplace/page
   * 首次查找可透传取消配置，增量分页保持原有调用方式
   */
  findReplacePage: (data: FindReplaceMaterialPageDto, config?: IRequestConfig) => {
    return request.post<PaginationData<FindReplaceMaterialPageRecord>>(
      '/creative/material/findReplace/page',
      data,
      config
    );
  },

  /**
   * 素材名称查找替换-批量替换
   * /creative/material/findReplace
   */
  findReplace: (data: FindReplaceMaterialDto) => {
    return request.post<FindReplaceMaterialResult>('/creative/material/findReplace', data);
  },

  /** 分页查询素材，页面可接管错误提示，其他调用保留默认请求行为 */
  page: (data: MaterialPageDto, config?: IRequestConfig) => {
    const requestData = JSON.parse(JSON.stringify(data));
    if (requestData.object?.uploadTimeBegin && !requestData.object.uploadTimeBegin.includes(':')) {
      requestData.object.uploadTimeBegin += ' 00:00:00';
      requestData.object.uploadTimeEnd += ' 23:59:59';
    }
    return request.post<MaterialPageRes>('/creative/material/page', requestData, config);
  },
  exportList,
  /**
   * 点赞或取消点赞素材
   * /material/like
   */
  likeChange: (data: {
    /**
     * true-点赞，false-取消点赞
     */
    liked: boolean;
    /**
     * 素材ID
     */
    materialId: string;
  }) => {
    return request.post('/creative/material/like', data);
  },
};

/** 获取设计师列表，允许选项区域接管加载失败提示 */
export const getDesignerList = <T = unknown>(nickname?: string, config?: IRequestConfig) => {
  //TODO 换成正式获取设计师接口后需要支持nickname搜索
  return request.get<T>('/creative/material/designer/list', config);
};

/**
 * 导出素材库列表（动态表头 Excel）
 */
export function exportList(
  data: MaterialExportDto,
  config?: Pick<IRequestConfig, 'showErrorToast' | 'errorContext'>
) {
  return request.postRaw<Blob>('creative/material/export', data, {
    ...config,
    responseType: 'blob',
    allowCancel: false,
  });
}
